import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import ClearIcon from '@mui/icons-material/Clear';
import SettingsIcon from '@mui/icons-material/Settings';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { AuthStoreContext } from '../../Root';
import KosaStore from '../../../stores/kosa-store';
import { Kosa } from '../../../types/kosa';
import { NodeChildrenSkeleton, NodeLoadError } from '../../dashboard/node/NodeItem';
import KosaSettingsDialog from './KosaSettingsDialog';

dayjs.extend(utc);

//=================================================================================================
function getStatusColor(lastTransaction: string): string {
    const diffHours = dayjs().diff(dayjs.utc(lastTransaction).local(), 'hour');
    if (diffHours < 24) return '#4caf50';
    if (diffHours < 48) return '#ff9800';
    return '#f44336';
}

interface KosaItemProps {
    kosa: Kosa;
    onConfig: (kosa: Kosa) => void;
}

function KosaItem({ kosa, onConfig }: KosaItemProps) {
    const statusColor = getStatusColor(kosa.lastTransaction);
    return (
        <ListItemButton sx={{ borderRadius: 1, py: 0.5, px: 1.5 }}>
            <DeviceThermostatIcon sx={{ fontSize: 14, mr: 0.5, flexShrink: 0 }} />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    flex: 1,
                    minWidth: 0,
                    mr: 1,
                }}
            >
                <Typography variant="body2" noWrap>
                    Коса-{kosa.id}
                </Typography>
                <Typography variant="caption" sx={{ ml: 1, flexShrink: 0 }}>
                    {kosa.sensorCount} дат
                </Typography>
            </Box>
            <Box
                sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0, ml: 1 }}
            />
            <Tooltip title="Настройки">
                <IconButton
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onConfig(kosa);
                    }}
                >
                    <SettingsIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
        </ListItemButton>
    );
}

//=================================================================================================
function KosaManager() {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user;

    const [store] = useState(() => new KosaStore(user?.node_id ?? 0));
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(false);
    const [filter, setFilter] = useState('');
    const [configKosa, setConfigKosa] = useState<Kosa | null>(null);

    useEffect(() => {
        if (!user) return;
        store
            .load()
            .catch(() => setError(true))
            .finally(() => setInitialLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) return null;

    const handleConfig = (kosa: Kosa) => {
        setConfigKosa(kosa);
    };

    let content: React.ReactNode;
    if (initialLoading) {
        content = <NodeChildrenSkeleton />;
    } else if (error) {
        content = <NodeLoadError />;
    } else if (store.items.length === 0) {
        content = (
            <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                    Нет данных
                </Typography>
            </Box>
        );
    } else {
        const filtered = filter ? store.items.filter((k) => String(k.id).startsWith(filter)) : store.items;
        content =
            filtered.length === 0 ? (
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        Ничего не найдено
                    </Typography>
                </Box>
            ) : (
                filtered.map((k) => <KosaItem key={k.id} kosa={k} onConfig={handleConfig} />)
            );
    }

    return (
        <>
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <Box sx={{ px: 3, py: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        Управление косами
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                        Изменение параметров косс.
                    </Typography>
                    <TextField
                        size="small"
                        placeholder="Фильтр по номеру"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value.replace(/\D/g, ''))}
                        slotProps={{
                            htmlInput: { inputMode: 'numeric', sx: { py: '4px' } },
                            input: {
                                endAdornment: filter ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setFilter('')}>
                                            <ClearIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            },
                        }}
                    />
                </Box>
                <Divider />
                <List dense disablePadding sx={{ p: 1 }}>
                    {content}
                </List>
            </Paper>
            {configKosa && <KosaSettingsDialog kosa={configKosa} onClose={() => setConfigKosa(null)} />}
        </>
    );
}

export default observer(KosaManager);
